import { PageIcon } from "@/components/PageIcon";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { History, Loader2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useLocations, sel } from "@/components/stock/StockActions";
import { MovementDirection, MovementType } from "@/components/stock/StockBadges";
import PrefTable from "@/components/PrefTable";
import { Pager } from "@/pages/stock/StockOverviewPage";

export default function StockMovementsPage() {
  const [sp] = useSearchParams();
  const [locations] = useLocations();
  const [types, setTypes] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [product, setProduct] = useState(sp.get("product") || "");
  const [type, setType] = useState("");
  const [locId, setLocId] = useState("");

  useEffect(() => { api.get("/stock/movement-types").then((r) => setTypes(r.data.types || [])); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/stock/movements", { params: { product_legacy_id: product || undefined, movement_type: type || undefined, location_id: locId || undefined, page } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [product, type, locId, page]);

  const MV_COLUMNS = [
    { key: "ts", label: "Date", defaultWidth: 170, render: (v) => <span className="text-xs text-slate-500 whitespace-nowrap">{new Date(v).toLocaleString("en-GB")}</span> },
    { key: "product_legacy_id", label: "Product", mandatory: true, defaultWidth: 200,
      render: (v, m) => <><Link to={`/products/${v}`} className="text-indigo-600 hover:underline truncate max-w-[180px] inline-block align-bottom">{m.product_title || v}</Link>{m.product_archived && <span className="block w-fit rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500" data-testid={`movement-archived-${m.movement_id}`}>Archived</span>}</> },
    { key: "movement_type", label: "Type", render: (v, m) => <span><MovementType type={v} label={m.type_label} />{m.transfer_ref && <span className="ml-1 text-[10px] text-slate-400">#{m.transfer_ref}</span>}</span> },
    { key: "quantity", label: "Qty", align: "right", render: (v, m) => <span className={`tabular-nums font-semibold ${m.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>{m.direction === "in" ? "+" : "−"}{v}</span> },
    { key: "direction", label: "Direction", render: (v) => <MovementDirection direction={v} /> },
    { key: "route", label: "From → To", render: (v, m) => <span className="text-xs text-slate-500">{m.from_location_name || "—"} → {m.to_location_name || "—"}</span> },
    { key: "job_legacy_id", label: "Job", render: (v) => (v ? <Link to={`/tasks/${v}`} className="text-indigo-600 hover:underline text-xs">{v}</Link> : "—") },
    { key: "user_email", label: "By", truncate: true, render: (v) => <span className="text-xs text-slate-400">{v}</span> },
  ];

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="stock-movements">
      <div className="flex items-center gap-3 mb-4"><PageIcon />
        <div><h1 className="text-2xl font-bold text-slate-900">Stock Movement History</h1><p className="text-sm text-slate-500">Complete, append-only audit trail of every stock change.</p></div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <input className={`${sel} max-w-[180px]`} placeholder="Product ID" value={product} onChange={(e) => { setPage(1); setProduct(e.target.value); }} data-testid="mv-filter-product" />
        <select className={`${sel} max-w-[220px]`} value={type} onChange={(e) => { setPage(1); setType(e.target.value); }} data-testid="mv-filter-type">
          <option value="">All movement types</option>
          {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <select className={`${sel} max-w-[220px]`} value={locId} onChange={(e) => { setPage(1); setLocId(e.target.value); }} data-testid="mv-filter-location">
          <option value="">All locations</option>
          {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden p-3">
        {loading ? <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          : <PrefTable tableId="stock_movements" columns={MV_COLUMNS} rows={data.items} rowKey={(m) => m.movement_id}
              rowTestId={(m) => `mv-row-${m.movement_id}`} emptyText="No movements match these filters." testid="stock-movements-table-wrap" />}
      </div>
      <Pager page={data.page} totalPages={data.total_pages} total={data.total} onPage={setPage} />
    </div>
  );
}
